// React
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Refine
import {
  useActiveAuthProvider,
  useNotification,
  useTranslation,
} from '@refinedev/core';
import {
  clearPendingSignIn,
  continueSignInWithMFASelection,
  getPendingSignInStep,
  resendMfaCode,
} from 'authProvider';
import { useDocumentTitle } from '@refinedev/react-router-v6';

// Mantine
import {
  Box,
  Flex,
  Card,
  Group,
  PinInput,
  Title,
  Text,
  Button,
  Stack,
  useComputedColorScheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';

// Components
import { QDocLogo } from 'components/common/QDocLogo';
import { TOTPSetup } from 'components/auth/TOTPSetup';

// Utils
import { getLoginCardBackground } from 'utils/styleUtils';
import { buttonTextColor } from 'utils/buttonUtils';

const RESEND_COOLDOWN_SECONDS = 30;
let pendingSignInCleanupTimer: ReturnType<typeof setTimeout> | null = null;

type VerifyMode = 'sms' | 'totp' | 'selection' | 'totp-setup';
type VerifyAuthResponse = {
  type:
    | 'onSuccess'
    | 'confirmSignInWithSMSCode'
    | 'confirmSignInWithTOTPCode'
    | 'continueSignInWithTOTPSetup'
    | 'signInSessionExpired';
};

const getModeFromPendingStep = (
  pendingStep: ReturnType<typeof getPendingSignInStep>
): VerifyMode => {
  switch (pendingStep?.type) {
    case 'verification':
      return pendingStep.mode;
    case 'selection':
      return 'selection';
    case 'totpSetup':
      return 'totp-setup';
    default:
      return 'sms';
  }
};

const isVerifyAuthResponse = (
  response: unknown
): response is VerifyAuthResponse => {
  if (
    typeof response !== 'object' ||
    response === null ||
    !('type' in response)
  )
    return false;

  return [
    'onSuccess',
    'confirmSignInWithSMSCode',
    'confirmSignInWithTOTPCode',
    'continueSignInWithTOTPSetup',
    'signInSessionExpired',
  ].includes(String(response.type));
};

interface VerifyProps {
  initialCooldown?: number;
  mode?: VerifyMode;
}

export const Verify: React.FC<VerifyProps> = ({
  initialCooldown = RESEND_COOLDOWN_SECONDS,
  mode: modeOverride,
}) => {
  const { translate } = useTranslation();
  useDocumentTitle(translate('pages:verify.documentTitle'));
  const computedColorScheme = useComputedColorScheme('dark');
  const backgroundImage = `url(/background-${computedColorScheme}.svg)`;
  const navigate = useNavigate();
  const location = useLocation();
  const { open } = useNotification();
  const authProvider = useActiveAuthProvider();

  const [isResending, setIsResending] = useState(false);
  const [cooldown, setCooldown] = useState(initialCooldown);
  const [isVerifying, setIsVerifying] = useState(false);
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);
  const autoSelectedMFA = useRef(false);
  const verificationInProgress = useRef(false);
  const recoveryStarted = useRef(false);
  const pendingStep = getPendingSignInStep();
  const locationMode = (
    location.state as {
      mode?: VerifyMode;
    } | null
  )?.mode;
  const mode =
    modeOverride ?? locationMode ?? getModeFromPendingStep(pendingStep);

  const recoverInterruptedSignIn = useCallback(() => {
    if (recoveryStarted.current) return;

    recoveryStarted.current = true;
    clearPendingSignIn();
    open?.({
      key: 'sign-in-session-interrupted',
      message: translate('pages:verify.notifications.sessionInterrupted'),
      type: 'error',
    });
    navigate('/login', { replace: true });
  }, [navigate, open, translate]);

  useEffect(() => {
    if (!pendingStep) {
      recoverInterruptedSignIn();
    }
  }, [pendingStep, recoverInterruptedSignIn]);

  useEffect(() => {
    if (pendingSignInCleanupTimer) {
      clearTimeout(pendingSignInCleanupTimer);
      pendingSignInCleanupTimer = null;
    }

    return () => {
      if (getPendingSignInStep()?.type === 'totpSetup') {
        pendingSignInCleanupTimer = setTimeout(() => {
          clearPendingSignIn();
          pendingSignInCleanupTimer = null;
        }, 0);
      }
    };
  }, []);

  const form = useForm({
    initialValues: {
      code: '',
    },
    validate: {
      code: (value: string) =>
        /^\S+$/.test(value)
          ? null
          : translate('pages:verify.validation.invalidCode'),
    },
  });

  const handleAuthResponse = useCallback(
    (response: VerifyAuthResponse) => {
      switch (response.type) {
        case 'onSuccess':
          navigate('/');
          return true;
        case 'confirmSignInWithSMSCode':
          navigate('/verify', { replace: true, state: { mode: 'sms' } });
          return true;
        case 'confirmSignInWithTOTPCode':
          navigate('/verify', { replace: true, state: { mode: 'totp' } });
          return true;
        case 'continueSignInWithTOTPSetup':
          navigate('/verify', {
            replace: true,
            state: { mode: 'totp-setup' },
          });
          return true;
        case 'signInSessionExpired':
          recoverInterruptedSignIn();
          return true;
        default:
          return false;
      }
    },
    [navigate, recoverInterruptedSignIn]
  );

  const handleVerify = async (value: string) => {
    if (verificationInProgress.current) return;

    verificationInProgress.current = true;
    setIsVerifying(true);
    try {
      const normalizedCode = value.replace(/[\s-]/g, '');
      const response = await authProvider.login({ code: normalizedCode });
      if (!isVerifyAuthResponse(response) || !handleAuthResponse(response)) {
        open?.({
          key: 'error-incorrect-code',
          message: translate('pages:verify.notifications.incorrectCode'),
          type: 'error',
        });
      }
    } catch (error) {
      open?.({
        key: 'error-verify-failed',
        message: translate('pages:verify.notifications.verificationFailed'),
        type: 'error',
      });
    } finally {
      verificationInProgress.current = false;
      setIsVerifying(false);
    }
  };

  const handleMFASelection = useCallback(
    async (mfaType: 'SMS' | 'TOTP') => {
      setSelectionLoading(true);
      try {
        const response = await continueSignInWithMFASelection(mfaType);
        if (!isVerifyAuthResponse(response) || !handleAuthResponse(response)) {
          throw new Error('Unable to continue sign in');
        }
      } catch {
        open?.({
          message: translate('pages:verify.notifications.mfaSelectionFailed'),
          type: 'error',
        });
      } finally {
        setSelectionLoading(false);
      }
    },
    [handleAuthResponse, open, translate]
  );

  useEffect(() => {
    if (
      mode === 'selection' &&
      pendingStep?.type === 'selection' &&
      pendingStep.allowedMFATypes.length === 1 &&
      !autoSelectedMFA.current
    ) {
      autoSelectedMFA.current = true;
      void handleMFASelection(pendingStep.allowedMFATypes[0]);
    }
  }, [mode, pendingStep, handleMFASelection]);

  const handleSignInTOTPSetup = async (code: string) => {
    setSelectionLoading(true);
    setSetupError(null);
    try {
      const response = await authProvider.login({ code });
      if (!isVerifyAuthResponse(response) || !handleAuthResponse(response)) {
        setSetupError(translate('pages:verify.totpSetup.invalidOrExpiredCode'));
      }
    } catch {
      setSetupError(translate('pages:verify.totpSetup.verificationFailed'));
    } finally {
      setSelectionLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || isResending) return;

    try {
      setIsResending(true);
      await resendMfaCode();

      open?.({
        message: translate('pages:verify.notifications.resendSucceeded'),
        type: 'success',
      });

      // restart cooldown using provided initial value
      setCooldown(initialCooldown);
    } catch (error) {
      open?.({
        message: translate('pages:verify.notifications.resendFailed'),
        type: 'error',
      });
    } finally {
      setIsResending(false);
    }
  };

  // Countdown timer for resend button
  useEffect(() => {
    if (cooldown <= 0) return;

    const interval = setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldown]);

  const isSMS = mode === 'sms';
  const canResend = isSMS && cooldown <= 0 && !isResending;
  const title = translate(`pages:verify.modes.${mode}.title`);
  const description = translate(`pages:verify.modes.${mode}.description`);
  const codeAriaLabel = translate(
    isSMS
      ? 'pages:verify.codeAriaLabels.sms'
      : 'pages:verify.codeAriaLabels.authenticator'
  );
  const getResendButtonLabel = (): string => {
    if (isResending) return translate('pages:verify.resend.sending');
    if (canResend) return translate('pages:verify.resend.action');
    return translate('pages:verify.resend.countdown', { seconds: cooldown });
  };
  const resendButtonLabel = getResendButtonLabel();

  if (!pendingStep) return null;

  return (
    <Box
      style={{
        backgroundImage: backgroundImage,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundRepeat: 'no-repeat',
        height: '100vh',
        backgroundSize: 'cover',
        backgroundColor: 'transparent',
      }}
    >
      <Flex
        direction="column"
        align="center"
        justify="center"
        style={{
          height: '100%',
        }}
      >
        <Card
          withBorder
          radius="xl"
          p="lg"
          style={{
            backdropFilter: 'blur(20px) saturate(160%)',
            WebkitBackdropFilter: 'blur(20px) saturate(160%)',
            background: getLoginCardBackground(computedColorScheme),
            borderColor:
              computedColorScheme === 'dark'
                ? 'var(--mantine-color-border-dark-0)'
                : 'var(--mantine-color-border-light-0)',
            boxShadow: 'var(--mantine-shadow-xl)',
            transition: 'transform 160ms ease',
            maxWidth: 420,
          }}
        >
          <Group align="center" gap="md" mt="lg" mb="xl">
            <QDocLogo />
            <div>
              <Title order={3}>{title}</Title>
              <Text size="sm" c="dimmed">
                {description}
              </Text>
            </div>
          </Group>

          {mode === 'selection' && pendingStep?.type === 'selection' ? (
            <Stack>
              {pendingStep.allowedMFATypes.includes('TOTP') && (
                <Button
                  loading={selectionLoading}
                  onClick={() => handleMFASelection('TOTP')}
                >
                  {translate('pages:verify.methods.authenticatorApp')}
                </Button>
              )}
              {pendingStep.allowedMFATypes.includes('SMS') && (
                <Button
                  loading={selectionLoading}
                  onClick={() => handleMFASelection('SMS')}
                >
                  {translate('pages:verify.methods.textMessage')}
                </Button>
              )}
            </Stack>
          ) : mode === 'totp-setup' && pendingStep?.type === 'totpSetup' ? (
            <TOTPSetup
              email={localStorage.getItem('email') ?? ''}
              secret={pendingStep.details.sharedSecret}
              error={setupError}
              submitting={selectionLoading}
              onCancel={() => {
                clearPendingSignIn();
                navigate('/login');
              }}
              onVerify={handleSignInTOTPSetup}
            />
          ) : (
            <form
              data-testid="verify-form"
              onSubmit={form.onSubmit((values) => handleVerify(values.code))}
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                gap: '2.5rem',
                marginBottom: '1.25rem',
              }}
            >
              <PinInput
                aria-label={codeAriaLabel}
                oneTimeCode
                placeholder="○"
                type="number"
                length={6}
                radius="xl"
                autoFocus
                disabled={isVerifying}
                onComplete={(value) => {
                  form.setFieldValue('code', value);
                  form.onSubmit((values) => handleVerify(values.code))();
                }}
                key={form.key('code')}
                {...form.getInputProps('code')}
                onPaste={(event) => {
                  const normalizedCode = event.clipboardData
                    .getData('text')
                    .replace(/[\s-]/g, '');
                  if (/^\d{6}$/.test(normalizedCode)) {
                    event.preventDefault();
                    form.setFieldValue('code', normalizedCode);
                    void handleVerify(normalizedCode);
                  }
                }}
                style={{ justifyContent: 'center' }}
              />

              {isSMS && (
                <Group justify="space-between" align="center">
                  <Text size="xs" c="dimmed">
                    {translate('pages:verify.resend.prompt')}
                  </Text>

                  <Button
                    disabled={!canResend}
                    onClick={handleResend}
                    variant="outline"
                    size="xs"
                    radius="md"
                    c={buttonTextColor(computedColorScheme, !canResend)}
                  >
                    {resendButtonLabel}
                  </Button>
                </Group>
              )}
            </form>
          )}
        </Card>
      </Flex>
    </Box>
  );
};
